<template>
  <KModal header="Switch Dashboard" v-model:visible="isVisible"
    ><div class="form-container">
      <!-- Error -->
      <BaseAlert v-if="isError" type="error" :message="errorMessage" />

      <p class="switch-org-modal__text">
        To manage groups, orders, and users associated with one of your other organizations or
        individual accounts, choose that option from the list below.
      </p>

      <form id="org-form" @submit="onSubmit">
        <BaseSelectValidated
          id="switch-org-modal__select"
          name="organization"
          label="Select Dashboard"
          :options="organizations"
          option-label="legal_name"
          :disabled="modalIsLoading"
          placeholder="-- Select --"
        />
      </form>
    </div>
    <template #footer>
      <div class="k-modal__buttons">
        <KButton label="Cancel" dismiss @click="hideModal" />
        <KButton type="submit" label="Submit" primary :loading="modalIsLoading" form="org-form" />
      </div> </template
  ></KModal>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { KModal, KButton } from "@k2united/frontend_components";
import BaseSelectValidated from "@/components/base/BaseSelectValidated.vue";
import BaseAlert from "@/components/base/BaseAlert.vue";
import { useServerError } from "@/composables/useServerError";
import { useUserStore } from "@/stores/user";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, string, number } from "yup";
import router from "@/router";
import useSwitchOrganizationMutation from "@/api/mutations/useSwitchOrganizationMutation";

const store = useUserStore();

interface Organization {
  legal_name: string;
  id: number;
}

const { mutate, isPending, error, isError } = useSwitchOrganizationMutation();
const organizations = computed(() => {
  if (store.user?.organizations !== undefined && store.user?.organizations.length > 0) {
    const orgs = store.user.organizations.map(({ legal_name, id }: Organization) => ({
      legal_name,
      id
    }));
    return orgs;
  }
  return [];
});

const modalIsLoading = computed(() => {
  return isPending.value || store.isLoading;
});

const { errorMessage } = useServerError(error);

// Modal
const isVisible = ref(false);

const hideModal = () => {
  isVisible.value = false;
};

const showModal = () => {
  isVisible.value = true;
  setCurrentOrgValues();
};

// Form
const validationSchema = toTypedSchema(
  object({
    organization: object({
      legal_name: string(),
      id: number().required().label("organization")
    })
  })
);

const { handleSubmit, setValues } = useForm({ validationSchema });

// Switch Orgs
const onSubmit = handleSubmit((selectedOrg) => {
  mutate(selectedOrg.organization.id, {
    onSuccess: async () => {
      await store.getUser();
      hideModal();
      router.push({ name: "Home" });
    }
  });
});

defineExpose({
  hideModal,
  showModal
});

const setCurrentOrgValues = () => {
  setValues({
    organization: {
      legal_name: store.user?.current_org?.legal_name,
      id: store.user?.current_org?.organization_id
    }
  });
};

watch(
  () => store.user?.organizations,
  () => {
    setCurrentOrgValues();
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.switch-org-modal__text {
  margin-bottom: 1rem;
}
</style>
